import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Scripture, ScriptureContext, getScriptureForContext } from '../../data/scriptures';

interface ScriptureLoaderProps {
  context?: ScriptureContext;
  className?: string;
}

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
`;

const LoaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  padding: 2rem;
  gap: 2rem;
`;

const SpinnerContainer = styled.div`
  position: relative;
  width: 60px;
  height: 60px;
`;

const Spinner = styled.div`
  border: 3px solid var(--background-secondary);
  border-top: 3px solid var(--color-accent);
  border-radius: 50%;
  width: 60px;
  height: 60px;
  animation: ${pulse} 1.5s ease-in-out infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ScriptureCard = styled.div`
  max-width: 600px;
  text-align: center;
  animation: ${fadeIn} 0.8s ease-out;
`;

const ScriptureText = styled.blockquote`
  font-size: 1.125rem;
  line-height: 1.6;
  color: var(--text-primary);
  font-style: italic;
  margin: 0 0 1rem 0;
  padding: 0 1rem;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const ScriptureReference = styled.cite`
  display: block;
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-weight: 600;
  font-style: normal;
  margin-top: 0.5rem;
`;

const LoadingText = styled.p`
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin-top: 1rem;
`;

/**
 * ScriptureLoader component displays a loading spinner with a contextually relevant Bible verse
 * Used during page transitions and data loading
 */
export const ScriptureLoader: React.FC<ScriptureLoaderProps> = ({
  context = 'general',
  className
}) => {
  const [scripture, setScripture] = useState<Scripture | null>(null);

  useEffect(() => {
    // Get a random scripture for the given context
    const selected = getScriptureForContext(context);
    setScripture(selected);
  }, [context]);

  if (!scripture) {
    return (
      <LoaderContainer className={className}>
        <SpinnerContainer>
          <Spinner />
        </SpinnerContainer>
      </LoaderContainer>
    );
  }

  return (
    <LoaderContainer className={className}>
      <SpinnerContainer>
        <Spinner />
      </SpinnerContainer>

      <ScriptureCard>
        <ScriptureText>"{scripture.text}"</ScriptureText>
        <ScriptureReference>— {scripture.reference}</ScriptureReference>
      </ScriptureCard>

      <LoadingText>Loading...</LoadingText>
    </LoaderContainer>
  );
};

export default ScriptureLoader;
